import type Hls from 'hls.js';
import type { ErrorData, HlsConfig, Level } from 'hls.js';

import { useStaleGuard } from '~/utils';

import {
	canHardwareDecodeHevc,
	hevcLevelsToRemove,
	resolveLevelIndices
} from './hevcDecodeGating';
import { fetchHlsResource } from './shakaLoader';

/**
 * hls.js atrás do MESMO contrato que o app consumia do Shaka (attach/load/
 * configure/unload/destroy, buffering/adaptation/error, variant tracks).
 * O QoE, o shared player e o useHlsPlayer não precisam conhecer o motor.
 * Rede passa por fetchHlsResource — o pipeline com cache/priority/bps que o
 * scheme plugin do Shaka aplicava.
 */

interface EngineConfig {
	streaming?: {
		rebufferingGoal?: number;
		bufferingGoal?: number;
		bufferBehind?: number;
		retryParameters?: Record<string, unknown>;
	};
	abr?: {
		enabled?: boolean;
		defaultBandwidthEstimate?: number;
		restrictions?: { maxBandwidth?: number };
		switchInterval?: number;
		bandwidthUpgradeTarget?: number;
		bandwidthDowngradeTarget?: number;
	};
}

export interface EngineVariantTrack {
	id: number;
	height: number | null;
	width: number | null;
	bandwidth: number;
	videoCodec?: string;
	active: boolean;
}

// One MediaCapabilities probe per session: the answer depends on the device,
// not on the video, and each attach() would otherwise wait on it again.
let hevcHardwareCheck: Promise<boolean> | null = null;

type EngineEvent = 'buffering' | 'adaptation' | 'error';

const DEFAULT_LOAD_TIMEOUT_MS = 30_000;
const MAX_RECOVERY_ATTEMPTS = 2;

let HlsCtor: typeof Hls | null = null;
let hlsPromise: Promise<typeof Hls> | null = null;

export function loadHlsEngine(): Promise<typeof Hls> {
	if (!import.meta.client) {
		return Promise.reject(new Error('hls.js can only be loaded on the client'));
	}
	if (!hlsPromise) {
		hlsPromise = import('hls.js').then(m => {
			HlsCtor = m.default;
			return m.default;
		});
	}
	return hlsPromise;
}

export function isEngineSupported(): boolean {
	return Boolean(HlsCtor?.isSupported());
}

function buildLoader(
	Ctor: typeof Hls,
	onBytes: (bytes: number) => void,
	isPreload: boolean
): HlsConfig['loader'] {
	// O hls.js nao diz "este e o primeiro fragmento", e so ele destrava o
	// primeiro frame: um contador por instancia de player marca os primeiros.
	// So o player que nao e preload ganha o boost — um vizinho fora de tela
	// nao deve competir com o player que o usuario esta assistindo.
	let mediaRequestCount = 0;

	return class PipelineLoader {
		context: unknown = null;
		stats = {
			aborted: false,
			loaded: 0,
			retry: 0,
			total: 0,
			chunkCount: 0,
			bwEstimate: 0,
			loading: { start: 0, first: 0, end: 0 },
			parsing: { start: 0, end: 0 },
			buffering: { start: 0, first: 0, end: 0 }
		};
		private abortController = new AbortController();
		// O hls.js destroi o loader logo apos o sucesso; sem esta guarda todo
		// fragmento concluido terminaria marcado como abortado e o ABR
		// descartaria a amostra de banda.
		private settled = false;
		private timeoutTimer: ReturnType<typeof setTimeout> | null = null;

		private clearTimer(): void {
			if (this.timeoutTimer !== null) {
				clearTimeout(this.timeoutTimer);
				this.timeoutTimer = null;
			}
		}

		load(context: any, config: any, callbacks: any): void {
			this.context = context;
			this.stats.loading.start = performance.now();
			// Contrato do hls.js: config e o LoaderConfiguration achatado (mesmo
			// shape de loadPolicy.default), maxLoadTimeMs no topo — nao aninhado.
			const timeoutMs = config?.maxLoadTimeMs ?? DEFAULT_LOAD_TIMEOUT_MS;
			this.timeoutTimer = setTimeout(() => {
				if (this.settled) return;
				this.settled = true;
				this.timeoutTimer = null;
				this.stats.aborted = true;
				this.abortController.abort();
				callbacks.onTimeout(this.stats, context, null);
			}, timeoutMs);
			// Manifests chegam como texto; fragmentos/init como arraybuffer.
			const isMediaRequest = context.responseType === 'arraybuffer';
			// Faixa de audio separada nao deve consumir o boost do fragmento de video.
			const isAudioRequest = context.frag?.type === 'audio';
			// Em CMAF o primeiro request e o init, entao o fragmento pesado seria o segundo.
			const isPriorityMediaRequest =
				!isPreload &&
				isMediaRequest &&
				!isAudioRequest &&
				++mediaRequestCount <= 2;
			void fetchHlsResource(context.url, {
				signal: this.abortController.signal,
				...(isPriorityMediaRequest ? { priority: 'high' as const } : {})
			})
				.then(({ uri, data, status }) => {
					if (this.settled) return;
					this.settled = true;
					this.clearTimer();
					const now = performance.now();
					this.stats.loading.first = this.stats.loading.first || now;
					this.stats.loading.end = now;
					this.stats.loaded = this.stats.total = data.byteLength;
					onBytes(data.byteLength);
					const payload =
						context.responseType === 'arraybuffer'
							? data
							: new TextDecoder().decode(data);
					callbacks.onSuccess(
						{ url: uri, data: payload, code: status },
						this.stats,
						context,
						null
					);
				})
				.catch((err: Error & { status?: number }) => {
					if (this.settled || err?.name === 'AbortError') return;
					this.settled = true;
					this.clearTimer();
					callbacks.onError(
						{ code: err.status ?? 0, text: err.message },
						context,
						null,
						this.stats
					);
				});
		}

		abort(): void {
			this.clearTimer();
			if (this.settled) return;
			this.settled = true;
			this.stats.aborted = true;
			this.abortController.abort();
		}

		destroy(): void {
			this.abort();
			this.context = null;
		}
	} as unknown as HlsConfig['loader'];
}

/**
 * Highest level index the ABR may use, given a bandwidth budget.
 *
 * `autoLevelCapping` is an index CEILING, and the ladder is not monotonic in
 * bitrate: hls.js sorts levels by height, so the H.264 fallback rung can sit
 * between two HEVC ones. One of our published masters reads 669k, 1401k, 1055k,
 * 2225k.
 *
 * The obvious reading is that taking the last index that fits admits a more
 * expensive level below the ceiling. It does not, and stopping at the first
 * level over budget is actively harmful:
 *
 *   - the ceiling is not what enforces the budget. Every candidate inside it
 *     still has to pass `adjustedbw >= bitrate` in findBestLevel, and the loop
 *     runs from the highest index down, so a cheaper rung above the expensive
 *     one is always reached first.
 *   - the ceiling decides the CODEC FAMILY. getCodecTiers only sees
 *     `levels.slice(minAutoLevel, maxAutoLevel + 1)`, so truncating at the
 *     first expensive rung can leave the H.264 rung as the only tier. The ABR
 *     never crosses codec families afterwards, and the session would be stuck
 *     outside HEVC for good.
 *   - measured on the ladder above, truncating early costs the 720p rung at
 *     1055k for measured bandwidth between 704 and 934 kbps, a 37% drop with
 *     no gain.
 */
export function levelCapForBandwidth(
	levels: ReadonlyArray<{ bitrate: number }>,
	maxBandwidth: number
): number {
	// -1 is "no cap" in hls.js, which is the honest answer for an empty ladder.
	if (!levels.length) return -1;
	// A budget that is not a finite number is not a budget. Returning the whole
	// ladder here would turn a missing value into "no limit at all".
	if (!Number.isFinite(maxBandwidth)) return 0;

	let cap = 0;
	for (let i = 0; i < levels.length; i++) {
		const bitrate = levels[i]!.bitrate;
		// A level without a usable bitrate is not counted as fitting.
		if (Number.isFinite(bitrate) && bitrate <= maxBandwidth) cap = i;
	}
	return cap;
}

export class EnginePlayer {
	constructor(private readonly isPreload = false) {}

	private hls: Hls | null = null;
	private media: HTMLMediaElement | null = null;
	private listeners: Record<EngineEvent, Set<(event: any) => void>> = {
		buffering: new Set(),
		adaptation: new Set(),
		error: new Set()
	};

	private pendingConfig: EngineConfig = {};
	private abrEnabled = true;
	private maxBandwidth: number | null = null;
	private mediaListeners: Array<[string, () => void]> = [];
	private loadingPaused = false;

	// Bytes que o player realmente puxou. Cruzado com o quanto foi assistido,
	// diz quanto da banda virou desperdicio.
	private bytesFetched = 0;

	getBytesFetched(): number {
		return this.bytesFetched;
	}
	// destroy() invalida: uma continuacao de attach()/load() de geracao velha
	// perdeu a corrida e nao pode anexar um Hls orfao ao elemento.
	private readonly staleGuard = useStaleGuard();
	private networkRecoveries = 0;
	private mediaRecoveries = 0;
	// Generation the HEVC gating already ran for. Keyed to the generation, not
	// a boolean: every load() advances it, so there is no reset to forget and a
	// load() that fails before its manifest cannot wedge the next one.
	private hevcGatedGeneration = -1;
	// Whether the device can hardware-decode HEVC, determined before Hls construction
	// per hls.js v1.7.0 API.md: videoPreference belongs in constructor config for
	// initial level selection, not post-parse mutation.
	// https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
	private hevcHardwareOk = false;
	// Whether the gating removed HEVC rungs for the current video (telemetry).
	private hevcLevelsRemoved = false;
	// Le pelo listener de ERROR permanente (attach), consumido pelo listener
	// temporario de load(): os dois disparam na mesma emissao sincrona do
	// hls.js, entao esta flag conta se a recuperacao ja foi tentada.
	private lastRecoveryAttempted = false;

	configure(config: EngineConfig): void {
		this.pendingConfig = {
			streaming: { ...this.pendingConfig.streaming, ...config.streaming },
			abr: { ...this.pendingConfig.abr, ...config.abr }
		};
		if (config.abr) {
			if (config.abr.enabled !== undefined) {
				this.abrEnabled = config.abr.enabled;
			}
			if (config.abr.restrictions !== undefined) {
				this.maxBandwidth = config.abr.restrictions.maxBandwidth ?? null;
			}
			if (this.hls) {
				// Voltar para "auto" precisa soltar o nivel pinado: no hls.js e o
				// currentLevel (-1) que reativa o ABR de fato, nao a flag local.
				if (config.abr.enabled === true) {
					this.hls.currentLevel = -1;
				}
				if (config.abr.defaultBandwidthEstimate) {
					this.hls.bandwidthEstimate = config.abr.defaultBandwidthEstimate;
				}
				this.applyBandwidthCap();
			}
		}
	}

	getConfiguration(): { abr: { enabled: boolean } } {
		return { abr: { enabled: this.abrEnabled } };
	}

	private applyBandwidthCap(): void {
		if (!this.hls) return;
		if (this.maxBandwidth === null) {
			this.hls.autoLevelCapping = -1;
			return;
		}
		const levels = this.hls.levels;
		// No ladder yet (before MANIFEST_PARSED): nothing to cap. The right
		// value is applied as soon as the levels exist.
		if (levels.length === 0) return;
		this.hls.autoLevelCapping = levelCapForBandwidth(levels, this.maxBandwidth);
	}

	/**
	 * Drops HEVC rungs the device can only software-decode (desktop Chromium
	 * without a hardware decoder) using hls.js documented removeLevel API, so
	 * the ABR stays on H.264 instead of stuttering on every CMAF segment.
	 * Runs once per manifest; keeps the ladder untouched when MediaCapabilities
	 * is missing or reports smooth hardware decode. removeLevel fires LEVELS_UPDATED,
	 * which re-applies the bandwidth cap.
	 *
	 * This is the documented removeLevel use case per hls.js v1.7.0 API.md:
	 * "removeLevel is for unsupported/error-prone renditions after parse."
	 * https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#hlsremovelevelindex
	 */

	/**
	 * Pin the first fragment before startLoad(). When HEVC HW is available,
	 * pick the lowest-height HEVC rung; otherwise fall back to 0 (cheapest).
	 * Must stay synchronous — hls.js resolves the start level on startLoad,
	 * which used to race an async MANIFEST_PARSED pin when startLevel was -1.
	 */
	private pinStartLevelSync(): void {
		if (!this.hls) return;
		const levels = this.hls.levels;
		if (!levels?.length) {
			this.hls.startLevel = 0;
			return;
		}
		if (this.hevcHardwareOk) {
			const hevc = levels
				.map((level, index) => ({ level, index }))
				.filter(({ level }) => /^(hvc1|hev1)\b/i.test(level.videoCodec || ''))
				.sort((a, b) => (a.level.height || 0) - (b.level.height || 0));
			if (hevc.length) {
				const start = hevc[0]!.index;
				this.hls.startLevel = start;
				this.hls.nextLoadLevel = start;
				return;
			}
		}
		this.hls.startLevel = 0;
		this.hls.nextLoadLevel = 0;
	}

	private async gateHevcDecode(): Promise<void> {
		if (!this.hls) return;
		// Captured without claiming a generation: load() owns it. One pass per
		// generation; and a video switch mid-await swaps the ladder, so a stale
		// generation drops the result below instead of stripping H.264 rungs.
		const generation = this.staleGuard.current();
		if (this.hevcGatedGeneration === generation) return;
		this.hevcGatedGeneration = generation;
		this.hevcLevelsRemoved = false;
		const capabilities =
			typeof navigator !== 'undefined' ? navigator.mediaCapabilities : null;
		const decodingInfo = capabilities?.decodingInfo
			? capabilities.decodingInfo.bind(capabilities)
			: null;
		const snapshot = this.hls.levels;
		const toRemove = await hevcLevelsToRemove(snapshot, decodingInfo);
		if (this.staleGuard.isStale(generation) || !this.hls) return;

		if (toRemove.length > 0) {
			// The generation only tracks video switches. Within the same load hls.js
			// can still drop a rung on its own (codec or key error), so a snapshot
			// index may now name another rung. Re-resolve by identity against the live
			// ladder; the helper skips rungs already gone and never returns them all.
			const indices = resolveLevelIndices(snapshot, toRemove, this.hls.levels);
			if (indices.length > 0) {
				// High-to-low so each index is still valid as the ladder shrinks.
				for (const index of indices.sort((a, b) => b - a)) {
					this.hls.removeLevel(index);
				}
				this.hevcLevelsRemoved = true;
			}
		}
	}

	/** Whether HEVC rungs were removed from the current ladder. */
	wasHevcGated(): boolean {
		return this.hevcLevelsRemoved;
	}

	async attach(media: HTMLMediaElement): Promise<void> {
		const generation = this.staleGuard.next();
		const Ctor = await loadHlsEngine();
		if (this.staleGuard.isStale(generation)) return;

		// Pre-check HEVC hardware decode capability before Hls construction.
		// hls.js v1.7.0 API.md: videoPreference limits initial level selection
		// when set in constructor config. Must be determined before loadSource.
		// https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
		const capabilities =
			typeof navigator !== 'undefined' ? navigator.mediaCapabilities : null;
		const decodingInfo = capabilities?.decodingInfo
			? capabilities.decodingInfo.bind(capabilities)
			: null;
		hevcHardwareCheck ??= canHardwareDecodeHevc(decodingInfo);
		this.hevcHardwareOk = await hevcHardwareCheck;
		if (this.staleGuard.isStale(generation)) return;

		this.media = media;
		this.loadingPaused = false;
		const streaming = this.pendingConfig.streaming ?? {};
		const abr = this.pendingConfig.abr ?? {};
		this.hls = new Ctor({
			loader: buildLoader(
				Ctor,
				bytes => {
					this.bytesFetched += bytes;
				},
				this.isPreload
			),
			maxBufferLength: streaming.bufferingGoal ?? 10,
			backBufferLength: streaming.bufferBehind ?? 30,
			abrEwmaDefaultEstimate: abr.defaultBandwidthEstimate ?? 2_000_000,
			// Hold startLoad until we pin synchronously on MANIFEST_PARSED.
			// startLevel: -1 + async pin raced hls.js (it starts on MANIFEST_LOADED).
			// Default 0 = cheapest; pinStartLevelSync upgrades to lowest HEVC when HW ok.
			// https://github.com/video-dev/hls.js/blob/v1.7.3/docs/API.md#autostartload
			autoStartLoad: false,
			startLevel: 0,
			enableWorker: true,
			testBandwidth: false,
			// hls.js v1.7.0 API.md: videoPreference limits initial level selection
			// to a codec family (if a baseline variant ≤1080p 30fps exists). Codec
			// switching mid-play generally requires manual level change. Setting
			// videoCodec to 'hvc1' (fourCC family) when HW decode is confirmed guides
			// initial selection toward HEVC instead of lingering on AVC ~640p (Safari/iOS).
			// https://github.com/video-dev/hls.js/blob/v1.7.0/docs/API.md#videopreference
			...(this.hevcHardwareOk
				? { videoPreference: { videoCodec: 'hvc1' } }
				: {})
		});
		if (this.staleGuard.isStale(generation)) {
			this.hls.destroy();
			this.hls = null;
			this.media = null;
			return;
		}
		this.hls.on(Ctor.Events.LEVEL_SWITCHED, () => {
			for (const cb of this.listeners.adaptation) cb({});
		});
		this.hls.on(Ctor.Events.MANIFEST_PARSED, () => {
			this.applyBandwidthCap();
			// Pin before startLoad so the first fragment is HEVC-lowest (or 0).
			this.pinStartLevelSync();
			if (!this.loadingPaused) this.hls?.startLoad();
			// Async gate only strips inefficient HEVC rungs; pin already ran.
			void this.gateHevcDecode();
		});
		// removeLevel (erro de codec, MediaCapabilities, erro de chave) encurta a
		// escada e desloca os indices sem tocar no autoLevelCapping, entao o teto
		// passaria a apontar para um degrau mais caro do que o escolhido.
		this.hls.on(Ctor.Events.LEVELS_UPDATED, () => {
			this.applyBandwidthCap();
		});
		this.hls.on(Ctor.Events.ERROR, (_event: unknown, data: ErrorData) => {
			if (!data.fatal) return;
			const message =
				'[hlsjs] ' +
				data.type +
				'/' +
				data.details +
				(data.response ? ' HTTP ' + data.response.code : '');
			const err = new Error(message);
			for (const cb of this.listeners.error) {
				cb({ detail: { message: err.message, stack: err.stack, data } });
			}
			// Sem teto, um 404/403 permanente vira laco apertado de recuperacao
			// e o QoE conta N erros para uma falha unica.
			this.lastRecoveryAttempted = false;
			if (data.type === Ctor.ErrorTypes.NETWORK_ERROR) {
				if (this.networkRecoveries < MAX_RECOVERY_ATTEMPTS) {
					this.networkRecoveries++;
					this.lastRecoveryAttempted = true;
					// Um pauseLoading() do governador de banda e deliberado — a
					// recuperacao nao pode reativar o fetch por cima dele.
					if (!this.loadingPaused) this.hls?.startLoad();
				}
			} else if (data.type === Ctor.ErrorTypes.MEDIA_ERROR) {
				if (this.mediaRecoveries < MAX_RECOVERY_ATTEMPTS) {
					this.mediaRecoveries++;
					this.lastRecoveryAttempted = true;
					this.hls?.recoverMediaError();
				}
			}
		});
		const onWaiting = () => {
			for (const cb of this.listeners.buffering) cb({ buffering: true });
		};
		const onPlaying = () => {
			for (const cb of this.listeners.buffering) cb({ buffering: false });
		};
		media.addEventListener('waiting', onWaiting);
		media.addEventListener('playing', onPlaying);
		media.addEventListener('canplay', onPlaying);
		this.mediaListeners = [
			['waiting', onWaiting],
			['playing', onPlaying],
			['canplay', onPlaying]
		];
		this.hls.attachMedia(media);
	}

	async load(url: string, startTime?: number): Promise<void> {
		if (!this.hls) throw new Error('attach() must run before load()');
		const generation = this.staleGuard.next();
		// Estado por reproducao: um EnginePlayer anexado uma vez pode receber
		// varios load() (troca de video na galeria) — nada disto pode vazar
		// da reproducao anterior para esta.
		this.networkRecoveries = 0;
		this.mediaRecoveries = 0;
		this.bytesFetched = 0;
		const Ctor = await loadHlsEngine();
		if (this.staleGuard.isStale(generation) || !this.hls) {
			throw new Error('[hlsjs] load superseded before it could start');
		}
		if (startTime !== undefined) {
			this.hls.config.startPosition = startTime;
		}
		await new Promise<void>((resolve, reject) => {
			const onParsed = () => {
				cleanup();
				resolve();
			};
			const onError = (_event: unknown, data: ErrorData) => {
				if (!data.fatal) return;
				// O listener permanente de attach() ja tentou recuperar esta mesma
				// emissao (ordem sincrona, FIFO) — so rejeita quando ele desistiu.
				if (this.lastRecoveryAttempted) return;
				cleanup();
				reject(
					new Error('[hlsjs] load failed: ' + data.type + '/' + data.details)
				);
			};
			const cleanup = () => {
				this.hls?.off(Ctor.Events.MANIFEST_PARSED, onParsed);
				this.hls?.off(Ctor.Events.ERROR, onError);
			};
			this.hls!.on(Ctor.Events.MANIFEST_PARSED, onParsed);
			this.hls!.on(Ctor.Events.ERROR, onError);
			this.hls!.loadSource(url);
		});
	}

	addEventListener(event: EngineEvent, cb: (event: any) => void): void {
		this.listeners[event]?.add(cb);
	}

	removeEventListener(event: EngineEvent, cb: (event: any) => void): void {
		this.listeners[event]?.delete(cb);
	}

	getVariantTracks(): EngineVariantTrack[] {
		if (!this.hls) return [];
		const current = this.hls.currentLevel;
		return this.hls.levels.map((level: Level, index: number) => ({
			id: index,
			height: level.height ?? null,
			width: level.width ?? null,
			bandwidth: level.bitrate,
			videoCodec: level.videoCodec,
			active: index === (current === -1 ? this.hls!.loadLevel : current)
		}));
	}

	selectVariantTrack(track: EngineVariantTrack, _clearBuffer?: boolean): void {
		if (!this.hls) return;
		this.hls.currentLevel = track.id;
	}

	/** Pausa/retoma o fetch de segmentos sem desfazer o attach (governador de banda). */
	// startLoad() do hls.js nao e idempotente: reinicia a maquina de estados e
	// aborta o fragmento em voo, entao so chamamos quando o estado muda.
	pauseLoading(): void {
		if (!this.hls || this.loadingPaused) return;
		this.loadingPaused = true;
		this.hls.stopLoad();
	}

	resumeLoading(): void {
		if (!this.hls || !this.loadingPaused) return;
		this.loadingPaused = false;
		this.hls.startLoad();
	}

	async unload(): Promise<void> {
		this.pauseLoading();
	}

	async destroy(): Promise<void> {
		this.staleGuard.next();
		if (this.media) {
			for (const [eventName, handler] of this.mediaListeners) {
				this.media.removeEventListener(eventName, handler);
			}
			this.mediaListeners = [];
		}
		this.hls?.destroy();
		this.hls = null;
		this.media = null;
		this.loadingPaused = false;
		for (const set of Object.values(this.listeners)) {
			set.clear();
		}
	}
}
